import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { FileAttachment } from '~hooks/useFileAttachment/models';
import { Nullable } from '~types/Nullable';
import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';
import { fileIsImage } from '~utils/utilFunctions';

const PREVIEW_MIN_HEIGHT_IN_PX = 480;

interface FileAttachmentPreviewDialogProps {
  attachments: FileAttachment[];
  isError: boolean;
  isFetching: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export default function FileAttachmentPreviewDialog({
  attachments,
  isError,
  isFetching,
  isOpen,
  onClose,
}: FileAttachmentPreviewDialogProps) {
  const [selectedId, setSelectedId] = useState<Nullable<string>>(null);

  const isSingleAttachment = attachments.length === 1;
  const selectedAttachment =
    attachments.find((attachment) => attachment.id === selectedId) ??
    (isSingleAttachment ? attachments[0] : null);

  const handleClose = () => {
    setSelectedId(null);
    onClose();
  };

  const openInNewTab = () => {
    window.open(selectedAttachment?.fileUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog isOpen={isOpen} maxWidth="md" onClose={handleClose}>
      <DialogHeader onClose={handleClose}>
        {selectedAttachment?.fileName ?? t('form_fields.attachments')}
      </DialogHeader>

      <DialogContent>
        {isFetching && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: PREVIEW_MIN_HEIGHT_IN_PX,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!isFetching && isError && (
          <Typography variant="body2">
            {t('error_messages.file_attachment.failed_to_fetch')}
          </Typography>
        )}

        {!isFetching && !isError && attachments.length === 0 && (
          <Typography variant="body2">{t('file_attachments.no_attachments')}</Typography>
        )}

        {!isFetching && !isError && selectedAttachment && (
          <AttachmentPreview attachment={selectedAttachment} />
        )}

        {!isFetching && !isError && !selectedAttachment && attachments.length > 0 && (
          <AttachmentList attachments={attachments} onSelect={setSelectedId} />
        )}
      </DialogContent>

      {selectedAttachment && (
        <DialogActions>
          {!isSingleAttachment && (
            <SmallButton
              color="brandV2Black"
              onClick={() => setSelectedId(null)}
              variant="outlined"
              sx={{ borderColor: 'divider', color: 'text.primary' }}
            >
              {t('actions.back')}
            </SmallButton>
          )}

          <SmallButton onClick={openInNewTab}>
            {t('file_attachments.open_in_new_tab')}
          </SmallButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

interface AttachmentListProps {
  attachments: FileAttachment[];
  onSelect: (id: string) => void;
}

function AttachmentList({ attachments, onSelect }: AttachmentListProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {attachments.map((attachment) => (
        <Box key={attachment.id}>
          <PseudoLink
            action={() => onSelect(attachment.id)}
            dataTestId={`file-attachment-option-${attachment.id}`}
            title={attachment.fileName}
          />
          {attachment.description && (
            <Typography variant="caption">{attachment.description}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

interface AttachmentPreviewProps {
  attachment: FileAttachment;
}

function AttachmentPreview({ attachment }: AttachmentPreviewProps) {
  const { fileName, fileUrl, mimeType } = attachment;

  if (mimeType === 'application/pdf') {
    // `previewUrl` is a 160x160 thumbnail variant, not the document, so the frame
    // has to load the file itself for the page to be readable.
    return (
      <Box
        component="iframe"
        src={fileUrl}
        title={fileName}
        sx={{
          border: 'none',
          height: '100%',
          minHeight: PREVIEW_MIN_HEIGHT_IN_PX,
          width: '100%',
        }}
      />
    );
  }

  if (fileIsImage(mimeType)) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: PREVIEW_MIN_HEIGHT_IN_PX,
        }}
      >
        <Box
          component="img"
          alt={fileName}
          crossOrigin={ATTACHMENT_IMAGE_CROSS_ORIGIN}
          src={fileUrl}
          sx={{ height: 'auto', maxWidth: '100%', objectFit: 'contain' }}
        />
      </Box>
    );
  }

  return (
    <Typography variant="body2">{t('file_attachments.preview_unavailable')}</Typography>
  );
}
